import React, { useState } from 'react';

import chapterImg2 from '../../../../assets/images/courses/team-players/chapter6.1.jpg';
import introImg2 from '../../../../assets/images/courses/team-players/chapter6.2.jpg';
import introImg from '../../../../assets/images/courses/team-players/chapter6.jpg';
import { CourseChapter } from '../../../../courses/courses.types';
import { MediaBlock } from '../../MediaBlock';
import { RatingTable, TopFiveResults } from '../../RoleCategory';
import { TabsBlock } from '../../TabsBlock';

interface TeamPlayersProps {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const TeamPlayersChapter6: React.FC<TeamPlayersProps> = ({
  chapter,
  onNextChapter,
  totalPages,
}) => {
  const [ratings, setRatings] = useState<{ [key: string]: number } | null>(null);
  const columns = ['Not like me', 'A little like me', 'A lot like me'];
  const roleCategories = [
    {
      title: 'Procrastination',
      roles: [
        {
          name: '',
          description:
            'I tend to procrastinate on tasks, which can lead to last-minute stress and rushed work',
        },
      ],
    },
    {
      title: 'Difficulty delegating tasks',
      roles: [
        {
          name: '',
          description:
            '"I often feel like I need to take on everything myself to ensure it´s donecorrectly, which can lead to burnout and inefficiency."',
        },
      ],
    },
    {
      title: 'Lack of confidence in public speaking',
      roles: [
        {
          name: '',
          description:
            '"I struggle with public speaking and often feel nervous or anxious when public presenting in front of others."',
        },
      ],
    },
    {
      title: 'Inability to multitask effectively',
      roles: [
        {
          name: '',
          description:
            '"I often find myself trying to juggle too many tasks at once, which can lead to mistakes and a lack of focus."',
        },
      ],
    },
    {
      title: 'Struggle with time management',
      roles: [
        {
          name: '',
          description:
            '"I sometimes struggle with managing my time effectively, which can lead to missed deadlines and a feeling of overwhelm."',
        },
      ],
    },
    {
      title: 'Difficulty adapting to change',
      roles: [
        {
          name: '',
          description:
            ' "I can sometimes struggle with adapting to new situations or changes in the workplace."',
        },
      ],
    },
    {
      title: 'Tendency to overthink or overanalyze situations',
      roles: [
        {
          name: '',
          description:
            '"I tend to overthink or overanalyze situations, which can lead to decision paralysis or unnecessary stress."',
        },
      ],
    },
    {
      title: 'Struggle with prioritizing tasks',
      roles: [
        {
          name: '',
          description:
            '"I sometimes struggle with prioritizing tasks effectively, which can lead to a feeling of overwhelm and missed deadlines."',
        },
      ],
    },
    {
      title: 'Difficulty saying "no" to requests or taking on too much',
      roles: [
        {
          name: '',
          description:
            '"I sometimes struggle with saying "no" to requests or taking on too much, which can lead to burnout and a lack of focus."',
        },
      ],
    },
    {
      title: 'Struggle with giving and receiving feedback',
      roles: [
        {
          name: '',
          description:
            '"I sometimes struggle with giving and receiving feedback, which can lead to misunderstandings and a lack of growth."',
        },
      ],
    },
    {
      title: 'Tendency to get easily distracted',
      roles: [
        {
          name: '',
          description:
            '"I sometimes find myself getting easily distracted by external factors, such as social media or personal tasks."',
        },
      ],
    },
    {
      title: 'Difficulty working in a team environment ',
      roles: [
        {
          name: '',
          description:
            '"I can sometimes struggle with working in a team environment, which can lead to misunderstandings and a lack of collaboration.',
        },
      ],
    },
    {
      title: 'Struggle with decision-making',
      roles: [
        {
          name: '',
          description:
            '"I sometimes struggle with making decisions, especially when there are multiple options or factors to consider."',
        },
      ],
    },
    {
      title: 'Inability to handle stress or pressure well',
      roles: [
        {
          name: '',
          description:
            '"I sometimes struggle with handling stress or pressure in the workplace, which can lead to burnout and a lack of productivity"',
        },
      ],
    },
    {
      title: 'Tendency to be too critical of oneself or others ',
      roles: [
        {
          name: '',
          description:
            '"I sometimes tend to be too critical of myself or others, which can lead to negative self-talk and a lack of motivation."',
        },
      ],
    },
    {
      title: 'Struggle with organization and planning ',
      roles: [
        {
          name: '',
          description:
            '"I sometimes struggle with organizing and planning my workload effectively, which can lead to missed deadlines and a lack of productivity."',
        },
      ],
    },
    {
      title: 'Difficulty with public speaking or presenting',
      roles: [
        {
          name: '',
          description:
            '"I sometimes struggle with public speaking or presenting, which can lead to nervousness and a lack of confidence."',
        },
      ],
    },
    {
      title: 'Tendency to avoid conflict or confrontation',
      roles: [
        {
          name: '',
          description:
            '"I sometimes tend to avoid conflict or confrontation, which can lead to unresolved issues and a lack of communication."',
        },
      ],
    },
    {
      title: 'Struggle with taking initiative or being proactive',
      roles: [
        {
          name: '',
          description:
            '"I sometimes struggle with taking initiative or being proactive in the workplace, which can lead to a lack of growth and development."',
        },
      ],
    },
    {
      title: ' Difficulty with maintaining work-life balance',
      roles: [
        {
          name: '',
          description:
            '"I sometimes struggle with maintaining a healthy work-life balance, which can lead to burnout and a lack of fulfillment in both areas."',
        },
      ],
    },
  ];

  const showYoureListeningTabData = [
    {
      title: 'AVOIDANCE:',

      htmlContent: `
 <p>The first option is to steer clear of situations that emphasize your weaknesses. For example, if you struggle with attention to detail or have a short fuse, you might want to skip that detailed stock check meeting. Similarly, if striking up conversations with strangers isn't your forte, consider avoiding tasks like manning the exhibition stand.</p>

`,
    },
    {
      title: 'SEEK SUPPORT:',

      htmlContent: ` <p>Option number two involves seeking assistance from your teammates. Perhaps there are colleagues who excel at handling the nitty-gritty details of stock checks or enjoy engaging with potential customers at the exhibition stand. This approach embodies the essence of teamwork, as you can reciprocate by assisting them in areas where they may struggle but you find relatively easy.</p>

`,
    },
    {
      title: 'SELF-IMPROVEMENT:',

      htmlContent: `<p>The third option is self-development, although some debate its effectiveness. While it's true that trying to teach a pig to sing may be futile and frustrating, the notion that you can't learn new skills is flawed. While you may not become a master without natural talent, you can certainly acquire enough proficiency to manage. However, consider whether it's necessary if your team members can cover your weaknesses. </p>
  `,
    },
  ];

  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };

  const handlePrints = () => {
    const printArea = document.getElementById('print-view');
    if (printArea) {
      printArea.classList.add('print-view');
      window.print();
      printArea.classList.remove('print-view');
    }
  };

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">Assessing & Managing Your Weaknesses</h2>
        <MediaBlock mediaImage={introImg}></MediaBlock>

        <br />
        <p className="text-xl text-center font-light mt-10 mb-10">
          Understanding and exploring your weaknesses is crucial when working in teams. It might
          seem uncomfortable at first, but recognizing areas where you struggle can make you a
          stronger team member.
        </p>
        <br />
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mb-1">Stress & Behavioral Style</h3>
        <br />
        <div className="md:flex items-center mt-10 mb-10">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg2} alt="" />
          </div>
          <p className="flex-1 leading-7">
            <br />
            When you're aware of your weaknesses, you can work on improving them, ask for help when
            needed, and learn how to complement your teammates' strengths. This self-awareness helps
            create a balanced and effective team, where everyone contributes their best.
            Additionally, being open about your weaknesses fosters trust and transparency within the
            team, leading to better communication and collaboration. Exploring your weaknesses is
            not about highlighting your flaws, but about growing and becoming a more reliable and
            supportive teammate.
          </p>
        </div>
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mb-1">Weakness Assessment</h3>
        <p className="text-lg text-center font-bold mt-10 mb-10">
          Rate the following weakness statements using the scale as follows:
        </p>
        <br />

        <div className="p-6 bg-white rounded-lg shadow-md mb-6 mt-6">
          {ratings ? (
            <TopFiveResults
              ratings={ratings}
              roleCategories={roleCategories}
              onRetake={() => setRatings(null)}
              showAllRatedRecords={16}
            />
          ) : (
            <RatingTable
              roleCategories={roleCategories}
              onSubmit={setRatings}
              column={columns}
              firstColumn="Weakness Statements"
            />
          )}
        </div>

        <h2 className="font-normal text-center text-2xl">
          The next section will provide some helpful information to help you approach and manage
          your weaknesses.
        </h2>
        <br />

        <h3 className="text-2xl text-purple-600 font-bold mt-10">
          Ways to Approach Your Weaknesses
        </h3>
        <MediaBlock mediaImage={introImg2}>
          {' '}
          <p className="text-xl text-left font-light mt-5 mb-5">
            "Knowing your weaknesses is as important as knowing your strengths. It’s in this
            awareness that true growth and collaboration occur."
            <br />
            <br />— Anonymous
          </p>
        </MediaBlock>

        <br />
        <p className="text-xl text-center font-light mt-10 mb-10">
          Understanding your weaknesses is crucial, perhaps even more so than knowing your
          strengths. Acknowledging and accepting areas where you may struggle, such as lacking
          attention to detail, becoming irritable when tired, or being overly optimistic, can be
          immensely valuable for you and your team. When everyone is aware of these weaknesses, it
          allows for better collaboration and support.
        </p>
        <br />
        <br />

        <h3 className="text-2xl text-purple-600 font-bold mt-10">Managing Weaknesses</h3>
        <p className="text-lg text-center font-bold mt-10 mb-10">
          Once you're aware of your weaknesses, you have three choices on how to manage them:
        </p>
        <p className="text-xl font-bold mb-8">Click each tab to learn more.</p>

        <TabsBlock tabsData={showYoureListeningTabData} />
        <h2 className="font-normal text-center text-2xl mt-10 mb-10">
          Reflect on whether you should accept limitations like disorganization or invest in
          learning time management to enhance your contributions to the team. Ultimately, it's a
          personal decision that may vary based on the circumstances. For instance, a reserved team
          member may resonate better with a technical customer than a more outgoing one.
        </h2>

        <h3 className="text-xl  font-bold mt-10 m-5">Embracing Challenge</h3>
        <p className="text-lg font-normal flex-1 mt-5 mb-5">
          Stepping out of your comfort zone is essential for personal growth and development. By
          embracing challenges and pushing yourself beyond familiar territory, you open doors to new
          experiences and skills. Without venturing outside your comfort zone, you limit your
          opportunities for learning and discovery. So, take the leap, try new things, and embrace
          the journey of self-development—it's the key to unlocking your full potential.
        </p>
        <h3 className="text-2xl text-purple-600 font-bold mt-10">Activity: Deal with Weaknesses</h3>
        <p className="text-lg font-normal flex-1 mt-5 mb-5">
          Complete the chart below to develop a plan. Reviewing your results from the Weakness
          Assessment above, make a list of your weaknesses in the left column and then enter how you
          will address each of them. Will you avoid tasks that require it? Will you get someone else
          to cover for you so the weakness doesn't matter? How will you develop yourself and get rid
          of that weakness?
        </p>
        <div id="print-view">
          <table className="seek-information w-full">
            <thead>
              <tr>
                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}>
                  My Weaknesses
                </th>
                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}>
                  How I will address them
                </th>
                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}></th>
                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}></th>
              </tr>
              <tr>
                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}></th>

                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}>Avoid</th>
                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}>
                  Cover -by who ?
                </th>
                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}>
                  Deveplo -how ?
                </th>
              </tr>
            </thead>
            <tbody className="bg-purple-100">
              <tr>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
              </tr>
              <tr>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
              </tr>
              <tr>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
              </tr>
              <tr>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="mt-5 text-right">
            <button onClick={handlePrints} className="btn-internal">
              PRINT
            </button>
          </div>
        </div>

        <br />
        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Understand the Value Others Bring to the Team
        </button>
      </div>
    </>
  );
};
